import { RESIDENTIAL_UNIT_TYPE_OPTIONS } from 'components/filterBar/residentialUnitTypes'
import { Filters } from 'types/general'

/** Mobile price dropdown slugs → monthly rent bounds (CAD), aligned with filter-options API */
const PRICE_BUCKET_BOUNDS: Record<string, { min?: number; max?: number }> = {
  '0to500': { min: 0, max: 500 },
  '500to1000': { min: 500, max: 1000 },
  '1000to1500': { min: 1000, max: 1500 },
  '1500to2000': { min: 1500, max: 2000 },
  '2000to3000': { min: 2000, max: 3000 },
  '3000to5000': { min: 3000, max: 5000 },
  over5000: { min: 5000 },
}

/** Backward-compatible bedroom filter values from older filter APIs/UI */
const BEDROOM_VALUE_MAP: Record<string, string> = {
  '0': 'studio',
  '1': '2.5',
  '2': '2.5',
  '3': '3.5',
  '4': '4.5',
  '5': '5.5',
  '5.5+': '6.5+',
}

/**
 * Builds a plain object for GET /properties?filters[...]=...
 * Returns null when no active criteria (load all listings).
 */
export function buildPropertyFilterParams(f: Filters): Record<string, unknown> | null {
  const filters: Record<string, unknown> = {}

  const city = f.location?.city?.trim()
  const neighborhoods = (f.location?.neighborhoods ?? [])
    .map(value => value.trim())
    .filter(Boolean)
  const legacyNeighborhood = f.location?.neighborhood?.trim()
  if (legacyNeighborhood && !neighborhoods.includes(legacyNeighborhood)) {
    neighborhoods.push(legacyNeighborhood)
  }
  const zip = f.location?.zip_code?.trim()
  if (city || neighborhoods.length > 0 || zip) {
    filters.location = {
      ...(city ? { city } : {}),
      ...(neighborhoods.length > 0 ? { neighborhoods } : {}),
      ...(zip ? { zip_code: zip } : {}),
    }
  }

  let priceMin = f.priceMin
  let priceMax = f.priceMax
  if (f.price?.length && f.price[0]) {
    const bucket = PRICE_BUCKET_BOUNDS[f.price[0]]
    if (bucket) {
      priceMin = bucket.min ?? priceMin
      priceMax = bucket.max ?? priceMax
    }
  }

  const minOk =
    priceMin !== undefined &&
    priceMin !== null &&
    Number.isFinite(Number(priceMin)) &&
    Number(priceMin) >= 0
  const maxOk =
    priceMax !== undefined &&
    priceMax !== null &&
    Number.isFinite(Number(priceMax)) &&
    Number(priceMax) >= 0

  if (minOk && maxOk && Number(priceMin) > Number(priceMax)) {
    // Invalid range — do not send price filters.
  } else {
    if (minOk) filters.priceMin = Number(priceMin)
    if (maxOk) filters.priceMax = Number(priceMax)
  }

  if (f.bedrooms?.length) {
    filters.bedrooms = f.bedrooms.map((value) => BEDROOM_VALUE_MAP[value] ?? value)
  }
  if (f.bathrooms?.length) filters.bathrooms = f.bathrooms
  if (f.unitType?.length) {
    const allowed = new Set(RESIDENTIAL_UNIT_TYPE_OPTIONS.map(opt => opt.value))
    const unitTypes = f.unitType
      .map(value => String(value).trim())
      .filter(value => allowed.has(value as (typeof RESIDENTIAL_UNIT_TYPE_OPTIONS)[number]['value']))
    if (unitTypes.length) filters.unitType = unitTypes
  }
  if (f.tags?.length) filters.tags = f.tags

  const boolEntries = Object.entries(f.booleans || {}).filter(([, v]) => v === true)
  if (boolEntries.length) {
    filters.booleans = Object.fromEntries(boolEntries)
  }

  const ce = f.contractEnd
  if (ce?.startDate && ce?.endDate) {
    filters.contractEnd = { startDate: ce.startDate, endDate: ce.endDate }
  }
  const av = f.availability
  if (av?.startDate && av?.endDate) {
    filters.availability = { startDate: av.startDate, endDate: av.endDate }
  }

  return Object.keys(filters).length ? filters : null
}
